import { safeDateParser, toTitleCaseString } from "@validereinc/utilities";
import classNames from "classnames/bind";
import { format } from "date-fns";
import { isPlainObject, set } from "es-toolkit/compat";
import { produce } from "immer";
import React, { useMemo, useState } from "react";
import { ListInputBare } from "../..";
import { Button } from "../../Button";
import {
  DateSelectorInputBare,
  type DateSelectorProps,
  type DateValueOrRange,
} from "../../DataInput/DateSelectorInput";
import { DropdownInputBare } from "../../DataInput/DropdownInput";
import TextInputBare from "../../DataInput/TextInput/TextInput";
import VariableTextInputBare from "../../DataInput/VariableTextInput/VariableTextInput";
import { Icon } from "../../Icon/Icon";
import { KeyValueList } from "../../KeyValueList/";
import { Radio } from "../../Radio";
import { Tooltip } from "../../Tooltip";
import {
  dataTypeOperatorInputTypeMapping,
  emptyFieldKey,
  equalityOperatorValue,
  generateSampleValueForFilterBuilder,
  getStringKeyFromArrayKey,
  safeJSONStringify,
  translateOperator,
} from "../FilterBuilder.helpers";
import styles from "../FilterBuilder.module.scss";
import { FilterBuilderDeleteFieldButton } from "../Subcomponents/FilterBuilderDeleteFieldButton";
import { FilterBuilderFieldPicker } from "../Subcomponents/FilterBuilderFieldPicker";
import {
  FilterBuilderAllowedOperatorsMapping,
  FilterBuilderFieldDefinition,
  FilterBuilderFieldsType,
  FilterBuilderFilterType,
  FilterBuilderInputModes,
  FilterBuilderInputModesType,
  FilterBuilderOperators,
  FilterBuilderProps,
  SupportedDateInputFormats,
} from "../types";

const cx = classNames.bind(styles);

export const FilterBuilderFieldOperatorAndValueInput = ({
  fieldDefinition,
  fieldKey,
  fieldValue,
  setFilter,
  onFilterChange,
  myKey,
  availableFields,
  onFieldNameChange,
  onRemoveField,
  fields,
  variant = "default",
  isDisabled,
  defaultSingleField,
  availableVariables,
  showInputModeToggle = true,
  operatorMappingOverride,
}: {
  fieldDefinition?: FilterBuilderFieldDefinition;
  myKey: Array<string | number>;
  fieldKey: string;
  fieldValue: any;
  setFilter: React.Dispatch<React.SetStateAction<FilterBuilderFilterType>>;
  onFilterChange?: FilterBuilderProps["onFilterChange"];
  availableFields?: string[];
  onFieldNameChange?: () => void;
  onRemoveField?: () => void;
  fields: FilterBuilderFieldsType;
  variant: "vertical" | "default";
  isDisabled?: boolean;
  defaultSingleField?: string;
  availableVariables?: string[];
  showInputModeToggle?: boolean;
  operatorMappingOverride?: FilterBuilderAllowedOperatorsMapping;
}) => {
  const operatorMapping = useMemo(() => {
    return operatorMappingOverride ?? dataTypeOperatorInputTypeMapping;
  }, [JSON.stringify(operatorMappingOverride)]);
  const isEmptyField = fieldKey === emptyFieldKey;

  // Calculate current value early to determine initial input mode
  const currentOperator = (
    isPlainObject(fieldValue)
      ? Object.keys(fieldValue)[0]
      : equalityOperatorValue
  ) as FilterBuilderOperators;

  const currentValue =
    currentOperator === equalityOperatorValue
      ? fieldValue
      : fieldValue[currentOperator];

  // Determine initial input mode based on whether the current value contains variables
  const initialInputMode =
    typeof currentValue === "string" &&
    currentValue.includes("$") &&
    availableVariables &&
    availableVariables.length > 0
      ? FilterBuilderInputModes.VARIABLE
      : FilterBuilderInputModes.TYPED_VALUE;

  // State to track the input mode for this field
  const [inputMode, setInputMode] =
    useState<FilterBuilderInputModesType>(initialInputMode);

  /**
   * A mapping from supported date formats of FilterBuilder, to variants supported by `<DateSelector />`
   */
  const dateFieldVariant: DateSelectorProps["variant"] = useMemo(() => {
    const mapping: Record<
      SupportedDateInputFormats,
      DateSelectorProps["variant"]
    > = {
      yyyy: "year",
      yyyyMM: "month",
      "yyyy-MM-dd": "day",
    };

    if (fieldDefinition && fieldDefinition.data_type === "date") {
      return mapping[fieldDefinition.format] ?? "day";
    }

    return "day";
  }, [fieldDefinition]);

  const availableOperators = useMemo(
    () =>
      fieldDefinition
        ? Object.keys(operatorMapping[fieldDefinition.data_type])
        : [],
    [fieldDefinition, operatorMapping]
  ) as FilterBuilderOperators[];

  const operatorsOptions: Array<{ value: string; label: string }> =
    availableOperators.map((op) => ({
      label: translateOperator(op),
      value: op,
    }));

  const activeInputType = useMemo(
    () =>
      fieldDefinition
        ? operatorMapping[fieldDefinition.data_type][currentOperator]
        : undefined,
    [fieldDefinition, operatorMapping, currentOperator]
  );

  const onValueChange = (keyToChange: string, newValue: any) => {
    setFilter((oldFilter: FilterBuilderFilterType) => {
      const producedFilter = produce(
        oldFilter,
        (draft: FilterBuilderFilterType) => {
          if (currentOperator === equalityOperatorValue) {
            set(draft, keyToChange, newValue);
          } else {
            set(draft, keyToChange, {
              [currentOperator]: newValue,
            });
          }
        }
      );

      onFilterChange?.(producedFilter);
      return producedFilter;
    });
  };

  // When a field definition is not available in "fields", it's rendered as read-only:
  if (!fieldDefinition && !isEmptyField)
    return (
      <div className={cx("readonly-condition")}>
        <KeyValueList
          variant="shaded"
          className={cx("readonly-key-value")}
          data={[
            {
              title: fieldKey,
              value: (
                <div className={cx("readonly-value-container")}>
                  <div className={cx("readonly-value")}>
                    {safeJSONStringify([fieldValue, null, 2])}
                  </div>
                  <div className={cx("info")}>
                    <Tooltip content="This field is not editable">
                      <Icon
                        variant="info"
                        size={20}
                      />
                    </Tooltip>
                  </div>
                </div>
              ),
            },
          ]}
        />
        {!isDisabled ? (
          <FilterBuilderDeleteFieldButton
            myKey={myKey}
            setFilter={setFilter}
            onFilterChange={onFilterChange}
            onDelete={onRemoveField}
          />
        ) : null}
      </div>
    );

  return (
    <div className={cx("operator-and-value-input")}>
      <div
        className={cx("inputs-area", {
          [variant]: !!variant,
          noFieldPicker: !!defaultSingleField,
        })}
      >
        {/* No matter what the render conditions are,
        the following 3 divs should be present to keep the layout intact:
         */}
        <div>
          <FilterBuilderFieldPicker
            fieldKey={isEmptyField ? "" : fieldKey}
            myKey={myKey}
            setFilter={setFilter}
            onFilterChange={onFilterChange}
            availableFields={availableFields}
            onChange={onFieldNameChange}
            fields={fields}
            isDisabled={isDisabled}
            defaultSingleField={defaultSingleField}
            operatorMappingOverride={operatorMappingOverride}
          />
        </div>
        <div>
          {fieldDefinition && (
            <DropdownInputBare
              isInline
              isFluid
              className={cx("dropdown")}
              name=""
              noSearchDebounce
              options={operatorsOptions}
              valueKey="value"
              labelKey="label"
              isClearable={false}
              isSearchable={false}
              onChange={(newOperator: FilterBuilderOperators) => {
                let safeValue = currentValue;

                const oldInputType =
                  operatorMapping[fieldDefinition.data_type][currentOperator];
                const newInputType =
                  operatorMapping[fieldDefinition.data_type][newOperator];

                const isSingleValueOperator = (op: FilterBuilderOperators) =>
                  op === equalityOperatorValue || op === "$eq" || op === "$ne";
                const switchedOperatorForCustomPickList =
                  newInputType === "custom-pick-list-input" &&
                  isSingleValueOperator(newOperator) !==
                    isSingleValueOperator(currentOperator);

                if (
                  newInputType !== oldInputType ||
                  switchedOperatorForCustomPickList
                ) {
                  safeValue = generateSampleValueForFilterBuilder({
                    data_type: fieldDefinition.data_type,
                    operator: newOperator,
                    formatTo:
                      fieldDefinition.data_type === "date"
                        ? fieldDefinition.format
                        : undefined,
                    operatorMappingOverride,
                  });
                }

                const keyToChange = getStringKeyFromArrayKey(myKey);
                setFilter((oldFilter: FilterBuilderFilterType) => {
                  const producedFilter = produce(
                    oldFilter,
                    (draft: FilterBuilderFilterType) => {
                      if (newOperator === equalityOperatorValue) {
                        set(draft, keyToChange, safeValue);
                      } else {
                        set(draft, keyToChange, {
                          [newOperator]: safeValue,
                        });
                      }
                    }
                  );

                  onFilterChange?.(producedFilter);
                  return producedFilter;
                });
              }}
              value={currentOperator}
              placeholder="Select Operator..."
              isDisabled={isDisabled}
            />
          )}
        </div>
        <div className={cx("value-input-container")}>
          {inputMode === FilterBuilderInputModes.VARIABLE &&
          availableVariables ? (
            <VariableTextInputBare
              className={cx("value-input")}
              name=""
              value={currentValue || ""}
              onChange={(newValue: string) => {
                const keyToChange = getStringKeyFromArrayKey(myKey);
                onValueChange(keyToChange, newValue);
              }}
              suggestions={availableVariables}
              placeholder="Enter value or start with $ for variables..."
              isDisabled={isDisabled}
            />
          ) : (
            <>
              {activeInputType === "string-input" && (
                <TextInputBare
                  className={cx("value-input")}
                  name=""
                  value={currentValue}
                  onChange={(newValue) => {
                    const keyToChange = getStringKeyFromArrayKey(myKey);
                    onValueChange(keyToChange, newValue);
                  }}
                  isDisabled={isDisabled}
                />
              )}

              {activeInputType === "number-input" && (
                <TextInputBare
                  className={cx("value-input")}
                  name=""
                  type="number"
                  formatType="number"
                  value={currentValue}
                  onChange={(newValue) => {
                    const keyToChange = getStringKeyFromArrayKey(myKey);
                    onValueChange(keyToChange, newValue);
                  }}
                  isDisabled={isDisabled}
                />
              )}

              {activeInputType === "list-input" && (
                <ListInputBare
                  className={cx("value-input")}
                  name=""
                  value={currentValue}
                  onChange={(newValue) => {
                    const keyToChange = getStringKeyFromArrayKey(myKey);
                    onValueChange(keyToChange, newValue);
                  }}
                  isDisabled={isDisabled}
                  placeholder="Hit enter to add a new item"
                />
              )}

              {activeInputType === "picklist-single-input" &&
                fieldDefinition && (
                  <DropdownInputBare
                    className={cx("dropdown", "value-input")}
                    name=""
                    noSearchDebounce
                    isMulti={false}
                    value={currentValue}
                    options={
                      "pick_list_options" in fieldDefinition
                        ? fieldDefinition.pick_list_options.map((option) => ({
                            value: option,
                            label:
                              ("pick_list_labels" in fieldDefinition
                                ? fieldDefinition.pick_list_labels?.[option]
                                : undefined) ??
                              toTitleCaseString(option, { intent: "ui" }),
                          }))
                        : []
                    }
                    labelKey="label"
                    valueKey="value"
                    onChange={(newValue) => {
                      const keyToChange = getStringKeyFromArrayKey(myKey);
                      onValueChange(keyToChange, newValue);
                    }}
                    isDisabled={isDisabled}
                  />
                )}

              {activeInputType === "picklist-multi-input" &&
                fieldDefinition && (
                  <DropdownInputBare
                    className={cx("dropdown", "value-input")}
                    name=""
                    noSearchDebounce
                    isMulti
                    value={currentValue}
                    options={
                      "pick_list_options" in fieldDefinition
                        ? fieldDefinition.pick_list_options.map((option) => ({
                            value: option,
                            label:
                              ("pick_list_labels" in fieldDefinition
                                ? fieldDefinition.pick_list_labels?.[option]
                                : undefined) ??
                              toTitleCaseString(option, { intent: "ui" }),
                          }))
                        : []
                    }
                    labelKey="label"
                    valueKey="value"
                    onChange={(newValue) => {
                      const keyToChange = getStringKeyFromArrayKey(myKey);
                      onValueChange(keyToChange, newValue);
                    }}
                    isDisabled={isDisabled}
                  />
                )}

              {activeInputType === "boolean-input" && (
                <Radio
                  className={cx("value-input", "radio-input")}
                  name=""
                  value={currentValue ? "true" : "false"}
                  onChange={(newValue) => {
                    const keyToChange = getStringKeyFromArrayKey(myKey);
                    const booleanValue = newValue === "true";
                    onValueChange(keyToChange, booleanValue);
                  }}
                  options={[
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                  ]}
                  labelKey="label"
                  valueKey="value"
                  isDisabled={isDisabled}
                />
              )}

              {activeInputType === "date-input" &&
                fieldDefinition?.data_type === "date" && (
                  <DateSelectorInputBare
                    className={cx("value-input")}
                    isRange={false}
                    variant={dateFieldVariant}
                    value={{
                      from: safeDateParser(
                        currentValue,
                        fieldDefinition.format,
                        new Date()
                      ).parsed,
                    }}
                    onChange={(newValue: DateValueOrRange | undefined) => {
                      const keyToChange = getStringKeyFromArrayKey(myKey);
                      if (typeof newValue === "undefined") {
                        onValueChange(keyToChange, "");
                      } else {
                        const formattedValue = format(
                          "from" in newValue ? newValue.from : newValue,
                          fieldDefinition.format
                        );
                        onValueChange(keyToChange, formattedValue);
                      }
                    }}
                    isDisabled={isDisabled}
                  />
                )}

              {activeInputType === "custom-pick-list-input" &&
                fieldDefinition?.data_type === "custom-pick-list" &&
                "renderInput" in fieldDefinition && (
                  <div
                    className={cx("value-input")}
                    key={`custom-pick-list-${currentOperator}`}
                  >
                    {fieldDefinition.renderInput({
                      value:
                        currentValue ??
                        (currentOperator === "$in" || currentOperator === "$all"
                          ? []
                          : ""),
                      onChange: (newValue) => {
                        const keyToChange = getStringKeyFromArrayKey(myKey);
                        onValueChange(keyToChange, newValue);
                      },
                      isDisabled,
                      isMulti:
                        currentOperator === "$in" || currentOperator === "$all",
                    })}
                  </div>
                )}
            </>
          )}

          {showInputModeToggle &&
            availableVariables &&
            availableVariables.length > 0 &&
            !isDisabled &&
            fieldDefinition &&
            fieldDefinition.data_type !== "custom-pick-list" && (
              <div className={cx("input-mode-toggle")}>
                <Tooltip
                  content={
                    inputMode === FilterBuilderInputModes.TYPED_VALUE
                      ? "Switch to Variable Mode"
                      : "Switch to Typed Value Mode"
                  }
                >
                  <Button
                    icon="swap"
                    variant="text"
                    size="small"
                    className={cx("mode-toggle-button")}
                    onClick={() => {
                      const newMode =
                        inputMode === FilterBuilderInputModes.TYPED_VALUE
                          ? FilterBuilderInputModes.VARIABLE
                          : FilterBuilderInputModes.TYPED_VALUE;
                      setInputMode(newMode);

                      // When switching to typed mode, generate appropriate sample value for the field type
                      // When switching to variable mode, clear the value
                      const keyToChange = getStringKeyFromArrayKey(myKey);
                      if (
                        newMode === FilterBuilderInputModes.TYPED_VALUE &&
                        fieldDefinition
                      ) {
                        const sampleValue = generateSampleValueForFilterBuilder(
                          {
                            data_type: fieldDefinition.data_type,
                            operator: currentOperator,
                            formatTo:
                              fieldDefinition.data_type === "date"
                                ? fieldDefinition.format
                                : undefined,
                            operatorMappingOverride,
                          }
                        );
                        onValueChange(keyToChange, sampleValue);
                      } else {
                        onValueChange(keyToChange, "");
                      }
                    }}
                  />
                </Tooltip>
              </div>
            )}
        </div>
      </div>
      {!isDisabled ? (
        <FilterBuilderDeleteFieldButton
          myKey={myKey}
          setFilter={setFilter}
          onFilterChange={onFilterChange}
          onDelete={onRemoveField}
        />
      ) : null}
    </div>
  );
};
